import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { BrandLockup } from "@/components/Brand";
import { Loader2, ArrowRight, Sparkles, KeyRound, Gamepad2 } from "lucide-react";
import { toast } from "sonner";

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [paths, setPaths] = useState([]);
  const [path, setPath] = useState(null);
  const [word, setWord] = useState("");
  const [signal, setSignal] = useState(null);
  const [busy, setBusy] = useState(false);
  const [event, setEvent] = useState(null);
  const [runId, setRunId] = useState(null);

  useEffect(() => { api.get("/onboarding/state").then((r) => { if (r.data.onboarded) navigate("/today"); setPaths(r.data.paths || []); }).catch(() => {}); }, [navigate]);

  const TOTAL = 5;
  const pct = Math.round((step / TOTAL) * 100);

  const decodeFirst = async () => {
    if (!word.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/decode/mini", { text: word, decode_type: "Personal Identity" });
      if (r.data.safety) { toast(r.data.safety.message); setBusy(false); return; }
      setSignal({ ...r.data.mini, root: r.data.rule_output.combined.litraroot, scores: r.data.rule_output.scores });
      setStep(2);
    } catch (e) { toast.error("Try another word"); } finally { setBusy(false); }
  };

  const startLife = async () => {
    setBusy(true);
    try {
      const r = await api.post("/life/start", { character_name: "You", path });
      setRunId(r.data.id); setEvent(r.data.current_event); setStep(4);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); } finally { setBusy(false); }
  };

  const makeChoice = async (idx) => {
    setBusy(true);
    try { await api.post("/life/choice", { run_id: runId, choice_index: idx }); } catch (e) { /* ignore */ }
    await finish();
  };

  const finish = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/finish", { path, first_word: word, primary_letter: (word[0] || "A").toUpperCase() });
      await refresh();
      toast.success("Welcome to Litrametry");
      navigate("/today");
    } catch (e) { toast.error("Could not finish onboarding"); setBusy(false); }
  };

  return (
    <div className="min-h-screen hero-sheen relative">
      <div className="grain" />
      <div className="relative max-w-xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6"><BrandLockup /><span className="mono-chip text-[var(--bone-faint)]">STEP {Math.min(step + 1, TOTAL)} / {TOTAL}</span></div>
        <Progress value={pct} className="h-1.5 bg-white/[0.06] mb-8" />

        {step === 0 && (
          <div className="fade-in">
            <h1 className="text-3xl font-display font-semibold">Choose your path</h1>
            <p className="text-[var(--bone-muted)] mt-2">This shapes your Path Mode journey. You can replay other paths anytime.</p>
            <div className="grid sm:grid-cols-2 gap-2 mt-5">
              {paths.map((p) => (
                <button key={p.id} onClick={() => { setPath(p.id); setStep(1); }} className="litra-card litra-card-hover p-4 text-left" data-testid={`onboarding-path-${p.id}`}>
                  <div className="font-display font-semibold">{p.name}</div>
                  <p className="text-sm text-[var(--bone-muted)] mt-1">{p.blurb}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="fade-in">
            <h1 className="text-3xl font-display font-semibold">Decode your first word</h1>
            <p className="text-[var(--bone-muted)] mt-2">Try your name, a word you live by, or pick one below.</p>
            <div className="flex gap-2 mt-5">
              <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder="e.g. your name…" className="bg-white/[0.04] border-white/[0.1]" data-testid="onboarding-word-input" onKeyDown={(e) => e.key === "Enter" && decodeFirst()} />
              <Button onClick={decodeFirst} disabled={busy} className="bg-[var(--primary)] hover:opacity-90" data-testid="onboarding-decode-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Decode"}</Button>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="onboarding-word-suggestions">
              {["Power", "Love", "Money", "Purpose", "Create", "Truth", "Voice", "Value", "Focus", "Name"].map((w) => (
                <button key={w} onClick={() => setWord(w)} data-testid={`onboarding-suggest-${w.toLowerCase()}`}
                  className={`mono-chip rounded-full px-3 py-1 border transition-colors ${word === w ? "border-[rgba(20,90,67,0.6)] green-text" : "border-white/[0.08] text-[var(--bone-faint)] hover:text-[var(--bone-muted)]"}`}>
                  {w}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && signal && (
          <div className="fade-in">
            <div className="mono-chip green-text">YOUR FIRST IDENTITY SIGNAL</div>
            <h1 className="text-3xl font-display font-semibold mt-1">{word}</h1>
            <div className="litra-card p-5 mt-4">
              <p className="text-[var(--bone-muted)]">{signal.overview}</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="glass rounded-full px-3 py-1 mono-chip">Class · {signal.word_class}</span>
                <span className="gold-edge gold-text rounded-full px-3 py-1 mono-chip">{signal.archetype}</span>
              </div>
            </div>
            <Button onClick={() => setStep(3)} className="mt-5 bg-[var(--primary)] hover:opacity-90" data-testid="onboarding-continue-signal">Reveal your first letter card <ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
        )}

        {step === 3 && (
          <div className="fade-in text-center">
            <KeyRound className="w-8 h-8 mx-auto gold-text" />
            <div className="mono-chip gold-text mt-3">FIRST LETTER CARD UNLOCKED</div>
            <div className="mx-auto mt-4 litra-card gold-edge p-8 w-40">
              <div className="text-6xl font-display font-semibold green-text">{(word[0] || "A").toUpperCase()}</div>
            </div>
            <p className="text-[var(--bone-muted)] mt-4">You’ve unlocked your first key. Collect all 26 in the Letter Hall.</p>
            <Button onClick={startLife} disabled={busy} className="mt-5 bg-[var(--primary)] hover:opacity-90" data-testid="onboarding-start-life">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start your first Life event <Gamepad2 className="w-4 h-4 ml-2" /></>}</Button>
          </div>
        )}

        {step === 4 && event && (
          <div className="fade-in">
            <div className="mono-chip green-text">AGE {event.age_stage} · YOUR FIRST CHOICE</div>
            <h1 className="text-2xl font-display font-semibold mt-1">{event.title}</h1>
            <p className="text-[var(--bone-muted)] mt-2">{event.scenario_text}</p>
            <div className="space-y-2 mt-4">
              {event.choices?.map((c, i) => (
                <button key={i} onClick={() => makeChoice(i)} disabled={busy} className="w-full text-left litra-card litra-card-hover p-4" data-testid={`onboarding-choice-${i + 1}`}>
                  <div className="flex items-center justify-between"><span className="text-sm">{c.label}</span><span className="mono-chip gold-text">+{c.xp} XP</span></div>
                </button>
              ))}
            </div>
            {busy && <div className="mt-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto" /></div>}
          </div>
        )}
      </div>
    </div>
  );
}
